import {assert} from 'assert-ts';
import type {
  AndCondition,
  Condition,
  NotCondition,
  OrCondition,
} from '../types';
import type {EvaluationArgs} from './types';
import {evaluateBooleanCondition} from './evaluateBooleanCondition';
import {evaluateComparisonCondition} from './evaluateComparisonCondition';
import {evaluateIncludesCondition} from './evaluateIncludesCondition';
import {evaluateLengthCondition} from './evaluateLengthCondition';
import {evaluateRangeCondition} from './evaluateRangeCondition';
import {evaluateRegexCondition} from './evaluateRegexCondition';
import {
  isAndCondition,
  isBooleanCondition,
  isComparisonCondition,
  isIncludesCondition,
  isLengthCondition,
  isNotCondition,
  isOrCondition,
  isRangeCondition,
  isRegexCondition,
} from './isConditionType';

export const evaluateNotCondition = (
  condition: NotCondition,
  args: EvaluationArgs,
): boolean => {
  return !evaluateCondition(condition.$not, args);
};

export const evaluateOrCondition = (
  condition: OrCondition,
  args: EvaluationArgs,
): boolean => {
  return condition.$or.some(cond => evaluateCondition(cond, args));
};

export const evaluateAndCondition = (
  condition: AndCondition,
  args: EvaluationArgs,
): boolean => {
  return condition.$and.every(cond => evaluateCondition(cond, args));
};

export const evaluateCondition = (
  condition: Condition | Condition[],
  args: EvaluationArgs,
): boolean => {
  // Array?
  if (Array.isArray(condition)) {
    return condition.reduce<boolean>(
      (acc, c) => acc && evaluateCondition(c, args),
      true,
    );
  }

  if (isRangeCondition(condition)) {
    return evaluateRangeCondition(condition, args);
  }

  if (isLengthCondition(condition)) {
    return evaluateLengthCondition(condition, args);
  }

  if (isRegexCondition(condition)) {
    return evaluateRegexCondition(condition, args);
  }

  if (isComparisonCondition(condition)) {
    return evaluateComparisonCondition(condition, args);
  }

  if (isNotCondition(condition)) {
    return evaluateNotCondition(condition, args);
  }

  if (isOrCondition(condition)) {
    return evaluateOrCondition(condition, args);
  }

  if (isAndCondition(condition)) {
    return evaluateAndCondition(condition, args);
  }

  if (isBooleanCondition(condition)) {
    return evaluateBooleanCondition(condition, args);
  }

  if (isIncludesCondition(condition)) {
    return evaluateIncludesCondition(condition, args);
  }

  assert.soft(false, 'evaluateCondition: unknown condition', {condition});

  return false;
};
